import { Directive, ElementRef, HostListener, Input } from '@angular/core';
import { KEY_CODE } from '@app/shared/constants/key-code.const';
import { getNextInputValue } from '@app/shared/utilities/input.utility';

const generalKeys = [
  'Backspace', // BACKSPACE
  'Tab', // TAB
  'Enter', // ENTER
  'Escape', // ESCAPE
  'Delete', // DELETE
  ':' // COLON
];

@Directive({
  selector: '[acsOnlyNumber]',
  standalone: false
})
export class OnlyNumberDirective {
  @Input() allowCommas: boolean;
  @Input() allowDashes: boolean;
  @Input() allowDecimal = true;
  @Input() allowNegative = false;
  @Input() allowSpaces: boolean;

  constructor(private elementRef: ElementRef) {}

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const pasteData = event.clipboardData?.getData('text') ?? '';

    if (!pasteData.length) return;

    this.validateInput(getNextInputValue(this.elementRef.nativeElement, pasteData), event);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (
      generalKeys.indexOf(event.key) !== -1 ||
      ((event.ctrlKey || event.metaKey) &&
        (event.key === KEY_CODE.A ||
          event.key === KEY_CODE.C ||
          event.key === KEY_CODE.V ||
          event.key === KEY_CODE.X ||
          event.key === KEY_CODE.Z)) ||
      event.key === KEY_CODE.RIGHT_ARROW ||
      event.key === KEY_CODE.LEFT_ARROW ||
      event.key === KEY_CODE.HOME ||
      event.key === KEY_CODE.END
    ) {
      return;
    }

    this.validateInput(getNextInputValue(this.elementRef.nativeElement, event.key), event);
  }

  private validateInput(nextValue: string, event: Event): void {
    if (!nextValue.length) return;

    const pattern = this.getAllowedPattern();

    if (!pattern.test(nextValue)) {
      event.preventDefault();
    }
  }

  /**
   * Creates regex to verify user inputted data against
   *
   * allowNegative: allows typing a dash for negative number if input is empty
   * allowDashes: allows typing in dash last character is not already a dash and it isn't the first character
   * allowCommas: allows typing if the last character is not already a comma
   * allowSPaces: not used anywhere
   */
  private getAllowedPattern(): RegExp {
    const negativeSign = this.allowNegative ? '-?' : '';
    const separatorChars = [
      this.allowCommas ? ',' : '',
      this.allowSpaces ? ' ' : '',
      this.allowDashes ? '\\-' : ''
    ].join('');

    const noConsecutiveDashes = this.allowDashes ? '(?!.*--)' : '';
    const noConsecutiveCommas = this.allowCommas ? '(?!.*,,)' : '';

    const integerPart = separatorChars ? `(?:\\d+(?:[${separatorChars}]?\\d*)*)?` : '\\d*';

    const decimalPart = this.allowDecimal ? '(?:\\.\\d*)?' : '';

    return new RegExp(
      `^${noConsecutiveDashes}${noConsecutiveCommas}${negativeSign}${integerPart}${decimalPart}$`
    );
  }
}
